"use client";

import { useCallback, useEffect, useState } from "react";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { AvatarPortrait } from "@/components/avatar-portrait";
import { SchemaEditor } from "@/components/schema-editor";
import { Badge, Button, Card, Spinner, inputClass } from "@/components/ui";

interface RegAvatar {
  id: string;
  name: string;
  gender: "female" | "male";
  ageRange: string;
  locale: string;
  premium: boolean;
  enabled: boolean;
  externalId?: string;
}

export default function AdminAvatarsPage() {
  const [items, setItems] = useState<RegAvatar[] | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    gender: "female",
    ageRange: "25-35",
    locale: "en-US",
    premium: false,
    externalId: "",
  });

  const load = useCallback(async () => {
    try {
      setItems(await adminFetch<RegAvatar[]>("/api/admin/assets/avatars"));
      setNeedKey(false);
    } catch (e) {
      if (e instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create() {
    setBusy("create");
    setNotice(null);
    try {
      await adminFetch("/api/admin/assets/avatars", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setForm({ ...form, name: "", externalId: "" });
      setNotice("Avatar added — live in the wizard now");
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Create failed");
    } finally {
      setBusy(null);
    }
  }

  async function toggle(a: RegAvatar) {
    setBusy(a.id);
    try {
      await adminFetch(`/api/admin/assets/avatars?id=${a.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !a.enabled }),
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function bind(a: RegAvatar) {
    const externalId = window.prompt(
      `HeyGen avatar_id for ${a.name}`,
      a.externalId ?? "",
    );
    if (externalId === null) return;
    setBusy(a.id);
    try {
      await adminFetch(`/api/admin/assets/avatars?id=${a.id}`, {
        method: "PATCH",
        body: JSON.stringify({ externalId }),
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function remove(a: RegAvatar) {
    if (!window.confirm(`Delete avatar "${a.name}"?`)) return;
    setBusy(a.id);
    try {
      await adminFetch(`/api/admin/assets/avatars?id=${a.id}`, {
        method: "DELETE",
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;
  if (!items) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight">
        Avatar container
      </h1>
      <p className="mt-2 text-sm text-muted">
        The container defines the recording format for digital-human assets
        across tech routes (model-hosted / reference-pack). Below it: the
        platform's preset roster, live in the wizard.
      </p>

      <SchemaEditor kind="avatar" />

      {notice && (
        <Card className="mt-4 px-4 py-3 text-[13px] text-muted">{notice}</Card>
      )}

      <h2 className="mt-10 mb-3 text-sm font-medium text-muted">
        Platform preset roster
      </h2>

      <Card className="p-5">
        <div className="mb-3 text-sm font-medium">New avatar</div>
        <div className="grid gap-3 sm:grid-cols-3">
          <input
            className={inputClass}
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <select
            className={inputClass}
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
          >
            <option value="female">Female</option>
            <option value="male">Male</option>
          </select>
          <input
            className={inputClass}
            placeholder="Age range, e.g. 25-35"
            value={form.ageRange}
            onChange={(e) => setForm({ ...form, ageRange: e.target.value })}
          />
          <input
            className={inputClass}
            placeholder="Locale, e.g. en-US"
            value={form.locale}
            onChange={(e) => setForm({ ...form, locale: e.target.value })}
          />
          <input
            className={inputClass}
            placeholder="External ID (HeyGen avatar_id)"
            value={form.externalId}
            onChange={(e) => setForm({ ...form, externalId: e.target.value })}
          />
          <label className="flex items-center gap-2 text-[13px] text-muted">
            <input
              type="checkbox"
              checked={form.premium}
              onChange={(e) => setForm({ ...form, premium: e.target.checked })}
            />
            Premium
          </label>
        </div>
        <Button
          className="mt-4"
          size="sm"
          disabled={busy === "create" || !form.name}
          onClick={() => void create()}
        >
          {busy === "create" ? <Spinner /> : "Add avatar"}
        </Button>
      </Card>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((a) => (
          <Card key={a.id} className="overflow-hidden p-0">
            <AvatarPortrait avatarId={a.id} className="h-28 w-full" />
            <div className="space-y-2 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{a.name}</span>
                <div className="flex gap-1.5">
                  {a.premium && <Badge tone="accent">Premium</Badge>}
                  <Badge tone={a.enabled ? "success" : "neutral"}>
                    {a.enabled ? "live" : "disabled"}
                  </Badge>
                </div>
              </div>
              <div className="text-[12px] text-muted">
                {a.gender} · {a.ageRange} · {a.locale}
              </div>
              <div className="truncate font-mono text-[11px] text-muted">
                ext: {a.externalId ?? "— not bound —"}
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy === a.id}
                  onClick={() => void toggle(a)}
                >
                  {a.enabled ? "Disable" : "Enable"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy === a.id}
                  onClick={() => void bind(a)}
                >
                  Bind ID
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="text-[var(--danger)]"
                  disabled={busy === a.id}
                  onClick={() => void remove(a)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
